<script setup lang="ts">
/**
 * Set a new password (Figma `66:46995` / `66:47069`).
 *
 * Calls the API directly rather than `useAuthStore().resetPassword`, which
 * stores the returned tokens and so signs the user straight in. The redesign
 * deliberately does not do that (`66:47522`): a credential change should be
 * followed by an explicit re-authentication, which also gives the backend a
 * natural point to invalidate sessions on other devices (`66:47542`).
 *
 * Expired, already-used and malformed tokens all land on the same Link expired
 * screen — telling them apart would leak which tokens ever existed (`66:47519`).
 */
import { FairButton } from '@/ui/primitives/FairButton'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthModal, useUserStore } from '@/features/auth/store'
import { resetPassword } from '@/features/auth/api/session'
import { isPasswordValid } from '@/features/auth/model/passwordRules'
import { isResetTokenRejected, mapAuthError } from '@/features/auth/model/mapAuthError'
import { useAuthFormValidation } from '@/features/auth/model/useAuthFormValidation'
import AuthField from '@/features/auth/ui/parts/AuthField.vue'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import PasswordRequirementChips from '@/features/auth/ui/parts/PasswordRequirementChips.vue'
import { useFairToast } from '@/ui/patterns/FairToast'

const RESET_PASSWORD_TOKEN_KEY = 'reset_password_token'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useFairToast()
const authModal = useAuthModal()
const userStore = useUserStore()

const token = ref('')
const password = ref('')
const passwordFocused = ref(false)

const address = computed(() => userStore.tempEmailForPasswordReset ?? '')

const clientValid = computed(() => isPasswordValid(password.value) && token.value !== '')
const form = useAuthFormValidation({ clientValid })

const chipsVisible = computed(() =>
  passwordFocused.value || (password.value !== '' && !isPasswordValid(password.value)),
)

onMounted(() => {
  const { resetToken } = route.params as { resetToken?: string | string[] }
  const fromRoute = Array.isArray(resetToken) ? resetToken[0] : resetToken
  token.value = fromRoute
    ?? window.localStorage.getItem(RESET_PASSWORD_TOKEN_KEY)
    ?? ''

  // Keep the token out of the address bar and out of the history entry.
  if (fromRoute) router.replace({ path: '/' })
})

async function handleSubmit() {
  await form.submit(async () => {
    try {
      await resetPassword({ token: token.value, password: password.value })
      window.localStorage.removeItem(RESET_PASSWORD_TOKEN_KEY)
      authModal.showAuthModal('passwordUpdated')
    } catch (error) {
      if (isResetTokenRejected(error)) {
        window.localStorage.removeItem(RESET_PASSWORD_TOKEN_KEY)
        authModal.showAuthModal('linkExpired')
        return
      }
      toast.show({ message: t(mapAuthError(error, 'resetPassword').messageKey), tone: 'warning' })
    }
  })
}
</script>

<template>
  <AuthScreenShell :title="t('auth.set_password.title')">
    <template
      v-if="address"
      #description
    >
      {{ t('auth.set_password.description', { email: address }) }}
    </template>

    <form
      class="fair-auth-form"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div class="fair-auth-form__field">
        <AuthField
          v-model="password"
          input-id="auth-set-password"
          type="password"
          autocomplete="new-password"
          :label="t('auth.set_password.password_label')"
          @focus="passwordFocused = true"
          @blur="passwordFocused = false"
        />
        <PasswordRequirementChips
          :password="password"
          :visible="chipsVisible"
        />
      </div>

      <FairButton
      class="fair-auth-cta"
      size="auto"
        type="submit"
        :label="t('auth.set_password.submit')"
        :disabled="form.submitDisabled.value"
      />
    </form>
  </AuthScreenShell>
</template>
